import React, { useState, useEffect, useRef } from 'react';
import { Navigate } from 'react-router-dom';
import { 
  Send, 
  Trash2, 
  Download, 
  LogOut, 
  MessageCircle, 
  CheckCheck, 
  Clock, 
  Wifi, 
  WifiOff, 
  Users,
  User,
  Crown,
  RefreshCw,
  Menu,
  X
} from 'lucide-react';
import io from 'socket.io-client';
import SidebarHeader from '../../components/admin/sidebar';
import { useAuth } from '../../pages/auth/AuthContext';

const SOCKET_URL = import.meta.env.VITE_SOCKET_URL || 'http://localhost:5000';

// Role-based access control component
const RequireAdminRole = ({ children, user, loading }) => {
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100 px-4">
        <div className="flex flex-col items-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600 text-center text-sm sm:text-base">Checking permissions...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  const userRole = user.role?.toLowerCase() || '';
  const isAdmin = userRole === 'admin' || userRole === 'super admin' || userRole === 'superadmin';
  
  if (!isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100 px-4">
        <div className="text-center max-w-md mx-auto">
          <div className="text-red-600 mb-4">
            <span className="text-4xl sm:text-6xl">🚫</span>
          </div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">Access Denied</h1>
          <p className="text-gray-600 mb-4 text-sm sm:text-base">
            You don't have permission to access the admin chat.
          </p>
          <p className="text-xs sm:text-sm text-gray-500 mb-6">
            Current role: <span className="font-medium">{user.role || 'No role assigned'}</span>
          </p>
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
            <button
              onClick={() => window.history.back()}
              className="bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-700 transition text-sm sm:text-base"
            >
              Go Back
            </button>
            <Navigate to="/" replace />
          </div>
        </div>
      </div>
    );
  }

  return children;
};

const AdminChat = () => {
  const { user: contextUser , token: contextToken, loading: authLoading, isAuthenticated } = useAuth();
  
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeTab] = useState('chat');
  const [currentUser , setCurrentUser ] = useState({
    name: contextUser ?.fullName || contextUser ?.name || 'CS Admin',
    email: contextUser ?.email || 'admin@telnet.co.id',
    role: contextUser ?.role || 'Admin'
  });

  const [customerChats, setCustomerChats] = useState(new Map()); // Map: customerId -> messages[]
  const [adminMessage, setAdminMessage] = useState('');
  const [isConnected, setIsConnected] = useState(false);
  const [onlineCustomers, setOnlineCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [stats, setStats] = useState({ 
    total: 0, 
    today: 0, 
    unread: 0, 
    guest: 0,
    online: { admins: 0, customers: 0, total: 0 }
  });
  const [adminName] = useState('CS Admin');
  const [connectionStatus, setConnectionStatus] = useState('connecting');
  const [isLoading, setIsLoading] = useState(false);
  
  const messagesEndRef = useRef(null);
  const socketRef = useRef(null);
  const typingTimeoutRef = useRef(null);

  // Update currentUser  from auth context
  useEffect(() => {
    if (contextUser ) {
      setCurrentUser ({
        name: contextUser .fullName || contextUser .name || 'CS Admin',
        email: contextUser .email || 'admin@telnet.co.id',
        role: contextUser .role || 'Admin'
      });
    }
  }, [contextUser ]);

  // Get messages untuk customer yang sedang dipilih
  const getCurrentMessages = () => {
    if (!selectedCustomer) return [];
    return customerChats.get(selectedCustomer) || [];
  };

  // Auto-scroll
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [getCurrentMessages()]);

  // Initialize Socket.IO
  useEffect(() => {
    const adminId = localStorage.getItem('adminId') || `admin_${Date.now()}`;
    localStorage.setItem('adminId', adminId);

    console.log('🆔 Admin ID:', adminId);

    socketRef.current = io(SOCKET_URL, {
      transports: ['polling', 'websocket'],
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 3000,
      timeout: 20000,
      autoConnect: true
    });

    // Connection events
    socketRef.current.on('connect', () => {
      console.log('✅ Admin connected to chat server');
      setIsConnected(true);
      setConnectionStatus('connected');
      
      socketRef.current.emit('join', {
        userId: adminId,
        userType: 'admin',
        userName: adminName
      });

      loadStats();
      loadOnlineCustomers();
    });

    socketRef.current.on('disconnect', (reason) => {
      setIsConnected(false);
      setConnectionStatus('disconnected');
    });

    // socketRef.current.on('connect_error', (error) => {
    //   console.error('❌ Connection error:', error);
    //   setConnectionStatus('error');
    // });

    // ✅ PERBAIKAN: Terima chat history untuk customer tertentu
    socketRef.current.on('customer_chat_history', (data) => {
      console.log('📖 Received customer chat history:', data.customerId, data.messages.length, 'messages');
      
      setCustomerChats(prev => {
        const newChats = new Map(prev);
        newChats.set(data.customerId, data.messages);
        return newChats;
      });
    });

    // ✅ PERBAIKAN: Terima pesan baru dari customer tertentu
    socketRef.current.on('new_message', (msg) => {
      console.log('📨 New message from customer:', msg.customerId, msg.senderName);
      
      const customerId = msg.customerId || msg.sender; // Gunakan customerId jika ada, fallback ke sender
      
      setCustomerChats(prev => {
        const newChats = new Map(prev);
        const existingMessages = newChats.get(customerId) || [];
        
        newChats.set(customerId, [
          ...existingMessages,
          {
            id: msg.id,
            text: msg.text,
            sender: msg.sender,
            senderName: msg.senderName,
            timestamp: msg.timestamp,
            status: msg.status || 'delivered',
            isGuest: msg.isGuest
          }
        ]);
        
        return newChats;
      });
      
      playNotificationSound();
    });

    // Message sent confirmation
    socketRef.current.on('message_sent', (msg) => {
      console.log('✅ Message sent confirmation:', msg);
      
      if (selectedCustomer) {
        setCustomerChats(prev => {
          const newChats = new Map(prev);
          const existingMessages = newChats.get(selectedCustomer) || [];
          
          const updatedMessages = existingMessages.map(m => 
            m.id === `temp_${msg.tempId}` ? { 
              ...msg,
              id: msg.id,
              status: 'delivered'
            } : m
          );
          
          newChats.set(selectedCustomer, updatedMessages);
          return newChats;
        });
      }
    });

    // Customer connected
    socketRef.current.on('customer_connected', (data) => {
      console.log('👤 Customer connected:', data);
      setOnlineCustomers(prev => {
        const existing = prev.find(c => c.id === data.customerId);
        if (existing) return prev;
        
        return [...prev, {
          id: data.customerId,
          name: data.customerName,
          socketId: data.socketId,
          isGuest: data.isGuest
        }];
      });
    });

    // Customer disconnected
    socketRef.current.on('customer_disconnected', (data) => {
      setOnlineCustomers(prev => prev.filter(c => c.id !== data.customerId));
      
      if (selectedCustomer === data.customerId) {
        setSelectedCustomer(null);
      }
    });

    // Online customers list
    socketRef.current.on('online_customers_list', (customers) => {
      console.log('👥 Received online customers list:', customers);
      setOnlineCustomers(customers);
    });

    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
      }
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    };
  }, []);

  // ✅ PERBAIKAN: Load chat history ketika customer dipilih
  useEffect(() => {
    if (selectedCustomer && socketRef.current?.connected) {
      console.log(`🔄 Loading chat history for customer: ${selectedCustomer}`);
      
      // Request chat history untuk customer yang dipilih
      socketRef.current.emit('request_customer_chat', {
        customerId: selectedCustomer
      });
    }
  }, [selectedCustomer]);

  // Handle select customer
  const handleSelectCustomer = (customerId) => {
    setSelectedCustomer(customerId);
    setAdminMessage(''); // Clear input ketika ganti customer
  };

  // Load chat stats
  const loadStats = async () => {
    try {
      setIsLoading(true);
      const response = await fetch(`${SOCKET_URL}/api/chat/admin/stats`);
      
      if (response.ok) {
        const data = await response.json();
        setStats({
          total: data.data.totalMessages || 0,
          today: data.data.todayMessages || 0,
          unread: data.data.unreadMessages || 0,
          guest: data.data.guestMessages || 0,
          online: data.data.online || { admins: 0, customers: 0, total: 0 }
        });
      }
    } catch (error) {
      console.error('Failed to load stats:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Load online customers
  const loadOnlineCustomers = async () => {
    try {
      const response = await fetch(`${SOCKET_URL}/api/chat/admin/online-customers`);
      
      if (response.ok) {
        const data = await response.json();
        setOnlineCustomers(data.data || []);
      }
    } catch (error) {
      console.error('Failed to load online customers:', error);
    }
  };

  // Refresh data
  const handleRefresh = () => {
    loadStats();
    loadOnlineCustomers();
  };

  // Play notification sound
  const playNotificationSound = () => {
    try {
      const audio = new Audio('/notification.mp3');
      audio.volume = 0.5;
      audio.play().catch(e => console.log('Sound play failed:', e));
    } catch (e) {
      console.log('Sound not available');
    }
  };

  // Send message to selected customer
  const handleSendAdminMessage = () => {
    if (!adminMessage.trim() || !socketRef.current?.connected || !selectedCustomer) {
      alert('Pilih customer terlebih dahulu!');
      return;
    }

    const tempId = Date.now();
    const currentMessages = getCurrentMessages();

    // Buat temporary message dengan status 'delivered' untuk menampilkan centang dua langsung
    const tempMessage = {
      id: `temp_${tempId}`,
      text: adminMessage,
      sender: 'admin',
      senderName: adminName,
      timestamp: new Date().toISOString(),
      status: 'delivered' // Ubah dari 'sending' ke 'delivered' untuk UI optimis
    };

    // Update state optimistically
    setCustomerChats(prev => {
      const newChats = new Map(prev);
      newChats.set(selectedCustomer, [...currentMessages, tempMessage]);
      return newChats;
    });
    
    // Kirim ke server
    socketRef.current.emit('send_message', {
      text: adminMessage,
      recipientId: selectedCustomer, // Kirim ke customer SPECIFIC
      tempId: tempId
    });

    setAdminMessage('');
    scrollToBottom();
  };

  // Handle key press
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendAdminMessage();
    }
  };

  // Clear chat
  const handleClearChat = async () => {
    if (!window.confirm('Yakin ingin menghapus semua riwayat chat? Tindakan ini tidak dapat dibatalkan.')) {
      return;
    }

    try {
      const response = await fetch(`${SOCKET_URL}/api/chat/admin/clear`, {
        method: 'DELETE'
      });

      if (response.ok) {
        setCustomerChats(new Map());
        alert('Riwayat chat berhasil dihapus');
        loadStats();
      }
    } catch (error) {
      console.error('Clear chat error:', error);
      alert('Terjadi kesalahan saat menghapus riwayat chat');
    }
  };

  // Export chat
  const handleExportChat = async () => {
    try {
      const response = await fetch(`${SOCKET_URL}/api/chat/admin/export`);
      
      if (response.ok) {
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `chat-export-${new Date().toISOString().split('T')[0]}.txt`;
        a.click();
        URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Export chat error:', error);
      alert('Terjadi kesalahan saat mengekspor chat');
    }
  };

  // Format time
  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('id-ID', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  // Get selected customer name
  const getSelectedCustomerName = () => {
    const customer = onlineCustomers.find(c => c.id === selectedCustomer);
    return customer ? customer.name : 'Pilih Customer';
  };

  // Get connection status text
  const getConnectionStatusText = () => {
    switch (connectionStatus) {
      case 'connected': return 'Terhubung';
      case 'connecting': return 'Menghubungkan...';
      case 'reconnecting': return 'Menyambung ulang...';
      case 'disconnected': return 'Terputus';
      case 'error': return 'Koneksi Error';
      default: return 'Menghubungkan...';
    }
  };

  const currentMessages = getCurrentMessages();

  // Mobile Header Component
  const MobileHeader = () => (
    <div className="lg:hidden bg-white shadow-sm border-b border-gray-200 px-4 py-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <Menu className="h-6 w-6" />
          </button>
          <div>
            <h1 className="text-lg font-semibold text-gray-900">Live Chat</h1>
            <p className="text-xs text-gray-600">{currentUser .role}</p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <div className="h-8 w-8 bg-green-600 rounded-full flex items-center justify-center">
            <span className="text-white text-sm font-medium">
              {currentUser .name.charAt(0).toUpperCase()}
            </span>
          </div>
        </div>
      </div>
    </div>
  );

  // Sidebar Overlay for Mobile
  const SidebarOverlay = () => (
    <>
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </>
  );

  return (
    <RequireAdminRole user={contextUser } loading={authLoading}>
      <div className="flex h-screen bg-gray-100 overflow-hidden">
        <SidebarOverlay />
        
        {/* Sidebar */}
        <div className={`
          fixed inset-y-0 left-0 z-50 w-64 transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        `}>
          <SidebarHeader 
            activeTab={activeTab} 
            currentUser ={currentUser }
            onClose={() => setSidebarOpen(false)}
          />
        </div>
        
        {/* Main Content */}
        <div className="flex-1 flex flex-col overflow-hidden">
          <MobileHeader />
          
          <main className="flex-1 overflow-auto p-3 sm:p-6 bg-gray-50">
            <div className="max-w-7xl mx-auto">
              {/* Stats Cards */}
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
                <div className="bg-white p-4 rounded-lg shadow border-l-4 border-blue-500">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Total Pesan</p>
                      <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
                    </div>
                    <MessageCircle className="text-blue-500" size={32} />
                  </div>
                </div>
                
                               <div className="bg-white p-4 rounded-lg shadow border-l-4 border-green-500">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Hari Ini</p>
                      <p className="text-2xl font-bold text-gray-900">{stats.today}</p>
                    </div>
                    <Clock className="text-green-500" size={32} />
                  </div>
                </div>
                
                <div className="bg-white p-4 rounded-lg shadow border-l-4 border-orange-500">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Belum Dibaca</p>
                      <p className="text-2xl font-bold text-gray-900">{stats.unread}</p>
                    </div>
                    <CheckCheck className="text-orange-500" size={32} />
                  </div>
                </div>
                
                <div className="bg-white p-4 rounded-lg shadow border-l-4 border-purple-500">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Pesan Guest</p>
                      <p className="text-2xl font-bold text-gray-900">{stats.guest}</p>
                    </div>
                    <User className="text-purple-500" size={32} />
                  </div>
                </div>
                
                <div className="bg-white p-4 rounded-lg shadow border-l-4 border-red-500">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Online</p>
                      <p className="text-2xl font-bold text-gray-900">{stats.online.customers}</p>
                      <button 
                        onClick={handleRefresh}
                        disabled={isLoading}
                        className="text-xs text-gray-500 hover:text-gray-700 flex items-center mt-1"
                      >
                        <RefreshCw size={12} className={`mr-1 ${isLoading ? 'animate-spin' : ''}`} />
                        {isLoading ? 'Loading...' : 'Refresh'}
                      </button>
                    </div>
                    <Users className="text-red-500" size={32} />
                  </div>
                </div>
              </div>

              {/* Main Chat Panel */}
              <div className="bg-white rounded-lg shadow-xl">
                {/* Header */}
                <div className="bg-gradient-to-r from-green-600 to-green-700 text-white p-4 rounded-t-lg flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center">
                      <Crown className="text-green-600" size={24} />
                    </div>
                    <div>
                      <h1 className="font-bold text-xl">Admin Panel - Customer Service</h1>
                      <div className="flex items-center space-x-2 text-sm">
                        {isConnected ? (
                          <Wifi size={14} className="text-green-100" />
                        ) : (
                          <WifiOff size={14} className="text-red-200" />
                        )}
                        <span className={connectionStatus === 'connected' ? 'text-green-100' : 'text-yellow-100'}>
                          {getConnectionStatusText()} • {currentMessages.length} pesan • {onlineCustomers.length} customer online
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={handleExportChat}
                      className="hover:bg-green-700 p-2 rounded transition-colors"
                      title="Export Chat"
                    >
                      <Download size={20} />
                    </button>
                    <button
                      onClick={handleClearChat}
                      className="hover:bg-green-700 p-2 rounded transition-colors text-red-200"
                      title="Clear All Chat"
                    >
                      <Trash2 size={20} />
                    </button>
                    <button
                      onClick={() => window.location.href = '/admin/logout'}
                      className="hover:bg-green-700 p-2 rounded transition-colors"
                      title="Logout"
                    >
                      <LogOut size={20} />
                    </button>
                  </div>
                </div>

                <div className="flex h-[600px]">
                  {/* Online Customers Sidebar */}
                  <div className="w-80 border-r border-gray-200 bg-gray-50 flex flex-col">
                    <div className="p-4 border-b border-gray-200">
                      <div className="flex items-center justify-between">
                        <h3 className="font-semibold text-gray-700 flex items-center">
                          <Users size={18} className="mr-2" />
                          Customers Online ({onlineCustomers.length})
                        </h3>
                        <button 
                          onClick={loadOnlineCustomers}
                          className="text-gray-500 hover:text-gray-700"
                          title="Refresh"
                        >
                          <RefreshCw size={14} />
                        </button>
                      </div>
                      <div className="flex items-center space-x-2 text-sm text-gray-600 mt-2">
                        <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                        <span>Online</span>
                        <div className="w-2 h-2 bg-gray-400 rounded-full ml-2"></div>
                        <span>Guest</span>
                      </div>
                    </div>
                    
                    <div className="flex-1 overflow-y-auto">
                      {onlineCustomers.length === 0 ? (
                        <div className="text-center text-gray-500 py-8">
                          <User size={48} className="mx-auto mb-4 opacity-30" />
                          <p className="text-sm">Tidak ada customer online</p>
                          <p className="text-xs mt-1">Tunggu customer bergabung...</p>
                        </div>
                      ) : (
                        <div className="space-y-2 p-2">
                          {onlineCustomers.map(customer => (
                            <button
                              key={customer.id}
                              onClick={() => handleSelectCustomer(customer.id)}
                              className={`w-full text-left p-3 rounded-lg transition-all ${
                                selectedCustomer === customer.id
                                  ? 'bg-green-100 border-2 border-green-300 shadow-sm'
                                  : 'bg-white hover:bg-gray-100 border border-gray-200'
                              }`}
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-2">
                                  <div className={`w-2 h-2 rounded-full ${
                                    customer.isGuest ? 'bg-gray-400' : 'bg-green-500'
                                  }`}></div>
                                  <span className="font-medium text-sm">{customer.name}</span>
                                </div>
                                {customer.isGuest && (
                                  <span className="bg-gray-200 text-gray-600 text-xs px-2 py-1 rounded">
                                    Guest
                                  </span>
                                )}
                              </div>
                              <p className="text-xs text-gray-500 mt-1 truncate">
                                ID: {customer.id.substring(0, 8)}...
                              </p>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Messages Area */}
                  <div className="flex-1 flex flex-col">
                    {/* Selected Customer Header */}
                    <div className="border-b border-gray-200 p-4 bg-white">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                            <User className="text-green-600" size={20} />
                          </div>
                          <div>
                            <h3 className="font-semibold text-gray-800">
                              {getSelectedCustomerName()}
                            </h3>
                            <p className="text-sm text-gray-500">
                              {selectedCustomer ? 'Sedang chat dengan customer' : 'Pilih customer untuk memulai chat'}
                            </p>
                          </div>
                        </div>
                        {selectedCustomer && (
                          <div className="flex items-center space-x-2">
                            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                            <span className="text-sm text-green-600">Online</span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Messages Container */}
                    <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
                      {!isConnected && connectionStatus === 'error' && (
                        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded text-sm">
                          ❌ Gagal terhubung ke server chat.
                        </div>
                      )}

                      {!selectedCustomer ? (
                        <div className="text-center text-gray-500 py-20">
                          <MessageCircle size={64} className="mx-auto mb-4 opacity-30" />
                          <h3 className="text-lg font-semibold mb-2">Pilih Customer</h3>
                          <p className="text-sm">Pilih customer dari sidebar untuk memulai percakapan</p>
                        </div>
                      ) : currentMessages.length === 0 ? (
                        <div className="text-center text-gray-500 py-20">
                          <MessageCircle size={64} className="mx-auto mb-4 opacity-30" />
                          <h3 className="text-lg font-semibold mb-2">Belum Ada Pesan</h3>
                          <p className="text-sm">Kirim pesan pertama kepada customer</p>
                        </div>
                      ) : (
                        currentMessages.map((msg) => (
                          <div
                            key={msg.id}
                            className={`flex ${msg.sender === 'admin' ? 'justify-end' : 'justify-start'}`}
                          >
                            <div
                              className={`max-w-[75%] rounded-lg p-3 ${
                                msg.sender === 'admin'
                                  ? 'bg-green-600 text-white rounded-br-none'
                                  : 'bg-white text-gray-800 rounded-bl-none shadow border'
                              }`}
                            >
                              <div className={`text-xs mb-1 font-medium ${
                                msg.sender === 'admin' ? 'text-green-100' : 'text-gray-500'
                              }`}>
                                {msg.senderName}
                                {msg.isGuest && msg.sender === 'customer' && (
                                  <span className="ml-2 bg-gray-200 text-gray-600 px-1 rounded text-xs">
                                    Guest
                                  </span>
                                )}
                              </div>
                              <p className="text-sm break-words whitespace-pre-wrap">{msg.text}</p>
                              <div className="flex items-center justify-end space-x-1 mt-1">
                                <span className={`text-xs ${
                                  msg.sender === 'admin' ? 'text-green-100' : 'text-gray-500'
                                }`}>
                                  {formatTime(msg.timestamp)}
                                </span>
                                {msg.sender === 'admin' && (
                                  <span>
                                    {msg.status === 'sending' && (
                                      <Clock size={12} className="text-green-100" />
                                    )}
                                    {(msg.status === 'sent' || msg.status === 'delivered') && (
                                      <CheckCheck size={12} className="text-green-100" />
                                    )}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        ))
                      )}
                      <div ref={messagesEndRef} />
                    </div>

                    {/* Input Area */}
                    <div className="p-4 border-t border-gray-200 bg-white">
                      <div className="flex items-center space-x-2">
                        <input
                          type="text"
                          value={adminMessage}
                          onChange={(e) => setAdminMessage(e.target.value)}
                          onKeyDown={handleKeyPress}
                          placeholder={
                            !selectedCustomer 
                              ? "Pilih customer terlebih dahulu..." 
                              : !isConnected 
                              ? "Menghubungkan..." 
                              : "Ketik balasan untuk customer..."
                          }
                          disabled={!isConnected || !selectedCustomer}
                          className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 disabled:bg-gray-100"
                          maxLength={1000}
                        />
                        <button
                          onClick={handleSendAdminMessage}
                          disabled={!adminMessage.trim() || !isConnected || !selectedCustomer}
                          className="bg-green-600 hover:bg-green-700 text-white p-3 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                        >
                          <Send size={20} />
                          <span>Kirim</span>
                        </button>
                      </div>
                      <div className="flex justify-between items-center mt-2">
                        <p className="text-xs text-gray-500">
                          {adminMessage.length}/1000 karakter
                        </p>
                        <p className="text-xs text-gray-500">
                          💬 Tekan Enter untuk mengirim • Chat terpisah per customer
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </main>
        </div>
      </div>
    </RequireAdminRole>
  );
};

export default AdminChat;